import * as THREE from 'three';

/**
 * Generate an SVG line drawing from a Three.js 3D object using EdgesGeometry.
 * Projects 3D edges to 2D via the provided camera, producing a clean
 * architectural-style vector drawing.
 *
 * @param {THREE.Object3D} object - The loaded 3D model
 * @param {THREE.Camera} camera - Camera defining the viewpoint
 * @param {Object} options
 * @param {number} options.width - SVG viewport width (default 800)
 * @param {number} options.height - SVG viewport height (default 600)
 * @param {number} options.edgeThreshold - Angle threshold in degrees for edge detection (default 15)
 * @param {string} options.strokeColor - Stroke color (default '#333333')
 * @param {number} options.strokeWidth - Stroke width (default 0.5)
 * @param {Map} [options.pieceColors] - Map of mesh UUID → hex color string for per-piece coloring
 * @param {Array} [options.pieces] - Detected pieces array for labeling
 * @param {string} [options.modelUnit] - Unit label for dimensions
 * @returns {string} SVG markup string
 */
export function generate3DEdgeSVG(object, camera, options = {}) {
  const {
    width = 800,
    height = 600,
    edgeThreshold = 15,
    strokeColor = '#333333',
    strokeWidth = 0.5,
    pieceColors = null,
  } = options;

  // Create an orthographic camera at a standard isometric angle
  // instead of using the scene's perspective camera
  const bbox = new THREE.Box3().setFromObject(object);
  const center = bbox.getCenter(new THREE.Vector3());
  const size = bbox.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);

  // Standard isometric angle (30° elevation, 45° azimuth)
  const distance = maxDim * 2;
  const azimuth = -Math.PI / 4;  // -45° — view from left side
  const elevation = Math.atan(1 / Math.sqrt(2)); // ~35.26° — true isometric

  // Create a temporary camera to compute the projected 2D bounds
  // Start with a large frustum, then we'll compute the actual bounds needed
  const tempCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, -maxDim * 10, maxDim * 10);
  tempCamera.position.set(
    center.x + distance * Math.cos(elevation) * Math.sin(azimuth),
    center.y + distance * Math.sin(elevation),
    center.z + distance * Math.cos(elevation) * Math.cos(azimuth)
  );
  tempCamera.lookAt(center);
  tempCamera.updateMatrixWorld(true);
  tempCamera.updateProjectionMatrix();

  // Project all 8 corners of the bounding box to find actual 2D bounds
  const corners = [
    new THREE.Vector3(bbox.min.x, bbox.min.y, bbox.min.z),
    new THREE.Vector3(bbox.min.x, bbox.min.y, bbox.max.z),
    new THREE.Vector3(bbox.min.x, bbox.max.y, bbox.min.z),
    new THREE.Vector3(bbox.min.x, bbox.max.y, bbox.max.z),
    new THREE.Vector3(bbox.max.x, bbox.min.y, bbox.min.z),
    new THREE.Vector3(bbox.max.x, bbox.min.y, bbox.max.z),
    new THREE.Vector3(bbox.max.x, bbox.max.y, bbox.min.z),
    new THREE.Vector3(bbox.max.x, bbox.max.y, bbox.max.z),
  ];

  // Transform corners to camera view space to find the 2D extents
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  corners.forEach(corner => {
    const viewPos = corner.clone().applyMatrix4(tempCamera.matrixWorldInverse);
    minX = Math.min(minX, viewPos.x);
    maxX = Math.max(maxX, viewPos.x);
    minY = Math.min(minY, viewPos.y);
    maxY = Math.max(maxY, viewPos.y);
  });

  // Calculate the required frustum size with minimal padding
  const padding = 1.02;
  const projWidth = (maxX - minX) * padding;
  const projHeight = (maxY - minY) * padding;
  const aspect = width / height;

  // Fit to the SVG aspect ratio while ensuring all content is visible
  let halfW, halfH;
  if (projWidth / projHeight > aspect) {
    // Model is wider than viewport - fit to width
    halfW = projWidth / 2;
    halfH = halfW / aspect;
  } else {
    // Model is taller than viewport - fit to height
    halfH = projHeight / 2;
    halfW = halfH * aspect;
  }

  const orthoCamera = new THREE.OrthographicCamera(-halfW, halfW, halfH, -halfH, -maxDim * 10, maxDim * 10);
  orthoCamera.position.copy(tempCamera.position);
  orthoCamera.lookAt(center);
  orthoCamera.updateMatrixWorld(true);
  orthoCamera.updateProjectionMatrix();

  const viewProjectionMatrix = new THREE.Matrix4();
  viewProjectionMatrix.multiplyMatrices(orthoCamera.projectionMatrix, orthoCamera.matrixWorldInverse);

  const lines = [];
  const faces = [];

  // Project a 3D point to 2D SVG coordinates
  const project = (point) => {
    const projected = point.clone().applyMatrix4(viewProjectionMatrix);
    return {
      x: (projected.x * 0.5 + 0.5) * width,
      y: (-projected.y * 0.5 + 0.5) * height,
      z: projected.z, // depth for potential culling
    };
  };

  // Traverse meshes and extract edges
  object.traverse((child) => {
    if (!child.isMesh || !child.geometry) return;

    const thresholdRad = (edgeThreshold * Math.PI) / 180;
    const edgesGeometry = new THREE.EdgesGeometry(child.geometry, thresholdRad);
    const positions = edgesGeometry.attributes.position;

    if (!positions || positions.count === 0) return;

    // Get world matrix for this mesh
    child.updateMatrixWorld(true);
    const worldMatrix = child.matrixWorld;

    // Determine color for this mesh
    let color = strokeColor;
    if (pieceColors && pieceColors.has(child.uuid)) {
      color = pieceColors.get(child.uuid);
    }

    // Extract line segments (pairs of vertices)
    for (let i = 0; i < positions.count; i += 2) {
      const p1 = new THREE.Vector3().fromBufferAttribute(positions, i);
      const p2 = new THREE.Vector3().fromBufferAttribute(positions, i + 1);

      // Transform to world space
      p1.applyMatrix4(worldMatrix);
      p2.applyMatrix4(worldMatrix);

      // Project to 2D
      const s1 = project(p1);
      const s2 = project(p2);

      // Skip lines behind the camera (both points behind near plane)
      if (s1.z < -1 && s2.z < -1) continue;
      if (s1.z > 1 && s2.z > 1) continue;

      // Skip degenerate lines
      const dx = s2.x - s1.x;
      const dy = s2.y - s1.y;
      if (Math.sqrt(dx * dx + dy * dy) < 0.1) continue;

      lines.push({ x1: s1.x, y1: s1.y, x2: s2.x, y2: s2.y, color, meshUuid: child.uuid });
    }

    edgesGeometry.dispose();

    // Extract face triangles for filled polygons (color mode only)
    if (pieceColors && pieceColors.has(child.uuid)) {
      const geo = child.geometry;
      const pos = geo.attributes.position;
      const idx = geo.index;
      if (pos) {
        const triCount = idx ? idx.count / 3 : pos.count / 3;
        for (let t = 0; t < triCount; t++) {
          const i0 = idx ? idx.getX(t * 3) : t * 3;
          const i1 = idx ? idx.getX(t * 3 + 1) : t * 3 + 1;
          const i2 = idx ? idx.getX(t * 3 + 2) : t * 3 + 2;

          const v0 = new THREE.Vector3().fromBufferAttribute(pos, i0).applyMatrix4(worldMatrix);
          const v1 = new THREE.Vector3().fromBufferAttribute(pos, i1).applyMatrix4(worldMatrix);
          const v2 = new THREE.Vector3().fromBufferAttribute(pos, i2).applyMatrix4(worldMatrix);

          const s0 = project(v0);
          const s1 = project(v1);
          const s2 = project(v2);

          if (s0.z < -1 && s1.z < -1 && s2.z < -1) continue;
          if (s0.z > 1 && s1.z > 1 && s2.z > 1) continue;

          faces.push({
            points: `${s0.x.toFixed(2)},${s0.y.toFixed(2)} ${s1.x.toFixed(2)},${s1.y.toFixed(2)} ${s2.x.toFixed(2)},${s2.y.toFixed(2)}`,
            z: (s0.z + s1.z + s2.z) / 3,
            meshUuid: child.uuid,
            color,
          });
        }
      }
    }

  });

  // Sort faces back-to-front
  faces.sort((a, b) => b.z - a.z);

  const svgFaces = faces.length > 0
    ? faces.map(f =>
        `<polygon points="${f.points}" fill="${f.color}" fill-opacity="0.05" stroke="none" data-mesh="${f.meshUuid}" />`
      ).join('\n    ')
    : '';

  // Build SVG edge lines
  const svgLines = lines
    .map(
      (l) =>
        `<line x1="${l.x1.toFixed(2)}" y1="${l.y1.toFixed(2)}" x2="${l.x2.toFixed(2)}" y2="${l.y2.toFixed(2)}" stroke="${l.color}" stroke-width="${strokeWidth}" data-mesh="${l.meshUuid}" />`
    )
    .join('\n    ');

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" preserveAspectRatio="xMidYMid meet" style="background:transparent;">
  ${svgFaces ? `<g>\n    ${svgFaces}\n  </g>` : ''}
  <g>
    ${svgLines}
  </g>
</svg>`;
}

/**
 * Capture a transparent PNG snapshot from a Three.js renderer as fallback.
 * @param {THREE.WebGLRenderer} renderer
 * @param {THREE.Scene} scene
 * @param {THREE.Camera} camera
 * @param {Object} options
 * @param {number} options.width - Output width (default 1600)
 * @param {number} options.height - Output height (default 1200)
 * @returns {string} Data URL (image/png)
 */
export function capture3DSnapshot(renderer, scene, camera, options = {}) {
  const { width = 1600, height = 1200 } = options;

  // Store original state
  const originalSize = new THREE.Vector2();
  renderer.getSize(originalSize);
  const originalClearColor = new THREE.Color();
  renderer.getClearColor(originalClearColor);
  const originalClearAlpha = renderer.getClearAlpha();
  const originalBackground = scene.background;

  // Set up for transparent render
  renderer.setSize(width, height);
  renderer.setClearColor(0x000000, 0);
  scene.background = null;

  // Render
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  renderer.render(scene, camera);

  // Capture
  const dataURL = renderer.domElement.toDataURL('image/png');

  // Restore original state
  renderer.setSize(originalSize.x, originalSize.y);
  renderer.setClearColor(originalClearColor, originalClearAlpha);
  scene.background = originalBackground;
  camera.aspect = originalSize.x / originalSize.y;
  camera.updateProjectionMatrix();

  return dataURL;
}

/**
 * Recolor an SVG string by replacing stroke colors based on mesh UUID → color mapping.
 * @param {string} svgString - SVG markup with data-mesh attributes
 * @param {Map<string, string>} meshColorMap - Map of mesh UUID → hex color string
 * @returns {string} Recolored SVG string
 */
export function recolorSVG(svgString, meshColorMap) {
  if (!meshColorMap || meshColorMap.size === 0) return svgString;

  // Replace stroke colors for lines
  let result = svgString.replace(
    /<line ([^>]*?)data-mesh="([^"]*?)"([^>]*?)\/>/g,
    (match, before, meshUuid, after) => {
      const newColor = meshColorMap.get(meshUuid);
      if (!newColor) return match;
      const full = before + `data-mesh="${meshUuid}"` + after;
      const replaced = full.replace(/stroke="[^"]*?"/, `stroke="${newColor}"`);
      return `<line ${replaced}/>`;
    }
  );

  // Replace fill colors for polygons
  result = result.replace(
    /<polygon ([^>]*?)data-mesh="([^"]*?)"([^>]*?)\/>/g,
    (match, before, meshUuid, after) => {
      const newColor = meshColorMap.get(meshUuid);
      if (!newColor) return match;
      const full = before + `data-mesh="${meshUuid}"` + after;
      const replaced = full.replace(/fill="[^"]*?"/, `fill="${newColor}"`);
      return `<polygon ${replaced}/>`;
    }
  );

  return result;
}
